import { gql } from '@apollo/client';
import IconCode from '@apollo/icons/default/IconCode.svg';
import IconDocument from '@apollo/icons/default/IconDocument.svg';
import IconGitHubSolid from '@apollo/icons/default/IconGitHubSolid.svg';
import IconLink from '@apollo/icons/default/IconLink.svg';
import IconTrash from '@apollo/icons/default/IconTrash.svg';
import { Divider, Heading, IconButton, Link } from '@apollo/orbit';
import { colors } from '@apollo/space-kit/colors';
import _ from 'lodash';
import * as React from 'react';
import { useLocation } from 'react-router';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { GraphRef } from '../../hooks/useGraphRef';

import { AddHomepageLinkModal } from './AddHomepageLinkModal';
import { DeleteHomepageLinkModal } from './DeleteHomepageLinkModal';

export const LinksContentGraphVariantFragment = gql`
  fragment LinksContentGraphVariantFragment on GraphVariant {
    id
    links {
      id
      title
      type
      url
    }
  }
`;

export const LinksSection = ({
  graphRef,
  links,
}: {
  graphRef: GraphRef;
  links:
    | GraphQLTypes.LinksContentGraphVariantFragment_links[]
    | undefined
    | null;
}) => {
  const [linkToDelete, setLinkToDelete] =
    React.useState<GraphQLTypes.LinksContentGraphVariantFragment_links | null>(
      null,
    );

  const onCloseDeleteModal = React.useCallback(() => {
    setLinkToDelete(null);
  }, []);

  const location = useLocation();

  return (
    <>
      <URLModal name={Config.modals.addGraphHomepageLink}>
        {({ isOpen, closeModal }) => (
          <AddHomepageLinkModal
            isOpen={isOpen}
            graphRef={graphRef}
            onClose={closeModal}
          />
        )}
      </URLModal>
      {linkToDelete && (
        <DeleteHomepageLinkModal
          isOpen
          link={linkToDelete}
          onClose={onCloseDeleteModal}
          graphRef={graphRef}
        />
      )}
      <div>
        <div className="flex justify-between">
          <Heading size="lg">Links</Heading>
          <PermissionGuard
            graphId={graphRef.graphId}
            graphVariant={graphRef.graphVariant}
            fallback={null}
            graphVariantPermissions="canUpdateVariantLinkInfo"
          >
            <Tooltip label="Create a new link" placement="left">
              <Link
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.addGraphHomepageLink,
                })}
                data-analytics-label="Sidebar: Links: Add link"
                data-analytics-category="Variant Home"
              >
                Add link
              </Link>
            </Tooltip>
          </PermissionGuard>
        </div>
        <Divider className="mt-1" />
        <div className="mt-3">
          {links?.length ? (
            _.sortBy(links, (link) =>
              link.type === GraphQLTypes.LinkInfoType.DEVELOPER_PORTAL
                ? -1
                : link.type === GraphQLTypes.LinkInfoType.REPOSITORY
                  ? 0
                  : 1,
            ).map((link) => {
              let LinkIcon;
              const linkUrl = new URL(link.url);
              switch (link.type) {
                case GraphQLTypes.LinkInfoType.DEVELOPER_PORTAL:
                  LinkIcon = IconDocument;
                  break;
                case GraphQLTypes.LinkInfoType.REPOSITORY:
                  LinkIcon =
                    linkUrl.host === 'github.com' ? IconGitHubSolid : IconCode;
                  break;
                default:
                  LinkIcon = IconLink;
                  break;
              }
              return (
                <div
                  key={link.id}
                  className="group mt-2 flex items-center justify-between overflow-hidden"
                >
                  <div className="flex items-center overflow-hidden">
                    <LinkIcon
                      width={16}
                      color={colors.grey.base}
                      className="mr-2"
                    />
                    <Link
                      isExternal
                      to={link.url}
                      className="truncate"
                      data-analytics-label={`Sidebar: Links: ${
                        {
                          DEVELOPER_PORTAL: 'link type:portal',
                          OTHER: 'link type:other',
                          REPOSITORY: 'link type:repo',
                        }[link.type]
                      }`}
                      data-analytics-category="Variant Home"
                    >
                      {link.type === GraphQLTypes.LinkInfoType.OTHER
                        ? link.title
                        : link.type ===
                            GraphQLTypes.LinkInfoType.DEVELOPER_PORTAL
                          ? 'Developer portal'
                          : link.url.replace(/https:\/\/|www.|.com/g, '')}
                    </Link>
                  </div>
                  <PermissionGuard
                    graphId={graphRef.graphId}
                    graphVariant={graphRef.graphVariant}
                    fallback={null}
                    graphVariantPermissions="canUpdateVariantLinkInfo"
                  >
                    <Tooltip label="Delete this link" placement="left">
                      <IconButton
                        onClick={() => setLinkToDelete(link)}
                        className="invisible group-hover:visible"
                        variant="hidden"
                        size="sm"
                        icon={<IconTrash />}
                        aria-label="Delete link"
                        data-analytics-label="Sidebar: Links: Delete link"
                        data-analytics-category="Variant Home"
                      />
                    </Tooltip>
                  </PermissionGuard>
                </div>
              );
            })
          ) : (
            <PermissionGuard
              graphId={graphRef.graphId}
              graphVariant={graphRef.graphVariant}
              graphVariantPermissions="canUpdateVariantLinkInfo"
              fallback={
                <div>
                  No links added yet.
                  <div className="italic text-neutral">
                    Know of an external site related to this graph? Ask a graph
                    admin to add it here...
                  </div>
                </div>
              }
            >
              <Link
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.addGraphHomepageLink,
                })}
              >
                Add an external reference link...
              </Link>
            </PermissionGuard>
          )}
        </div>
      </div>
    </>
  );
};
